import { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Home, RefreshCw } from 'lucide-react';
import { API } from '../lib/constants';
import { Footer } from '../components/Footer';
import { Hero } from '../components/sections/Hero';
import { Navigation } from '../components/Navigation';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const RealEstatePage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest_type: '',
    property_type: '',
    budget_range: '',
    location: '',
    message: '',
    consent_given: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent_given) {
      toast.error(t.realEstate.error);
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/real-estate-inquiry`, {
        ...formData,
        language
      });
      toast.success(t.realEstate.success);
      setFormData({
        name: '',
        email: '',
        phone: '',
        interest_type: '',
        property_type: '',
        budget_range: '',
        location: '',
        message: '',
        consent_given: false
      });
    } catch (error) {
      const message = error.response?.data?.detail || t.realEstate.error;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-40 pb-24 px-6 md:px-12 dark-grid-section">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-20 h-20 bg-[#5B7FA8]/20 rounded-2xl flex items-center justify-center mx-auto mb-8 backdrop-blur-sm border border-[#5B7FA8]/30"
          >
            <Home className="w-10 h-10 text-[#38BDF8]" />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-5xl md:text-6xl font-bold mb-6 tracking-tight text-white"
          >
            {t.realEstate.title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-slate-300 mb-6"
          >
            {t.realEstate.subtitle}
          </motion.p>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-lg text-slate-400 max-w-2xl mx-auto"
          >
            {t.realEstate.description}
          </motion.p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-dark rounded-3xl p-8 md:p-12"
          >
            <h2 className="text-3xl font-bold text-white mb-8">
              {t.realEstate.formTitle}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="re-name" className="block text-sm font-semibold text-slate-300 mb-2">
                  {t.realEstate.name} *
                </label>
                <input
                  type="text"
                  id="re-name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t.realEstate.namePlaceholder}
                  data-testid="re-name-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="re-email" className="block text-sm font-semibold text-slate-300 mb-2">
                    {t.realEstate.email} *
                  </label>
                  <input
                    type="email"
                    id="re-email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder={t.realEstate.emailPlaceholder}
                    data-testid="re-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="re-phone" className="block text-sm font-semibold text-slate-300 mb-2">
                    {t.realEstate.phone} *
                  </label>
                  <input
                    type="tel"
                    id="re-phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={t.realEstate.phonePlaceholder}
                    data-testid="re-phone-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="re-interest" className="block text-sm font-semibold text-slate-300 mb-2">
                    {t.realEstate.interest} *
                  </label>
                  <select
                    id="re-interest"
                    required
                    value={formData.interest_type}
                    onChange={(e) => setFormData({ ...formData, interest_type: e.target.value })}
                    data-testid="re-interest-select"
                  >
                    <option value="">{t.realEstate.interestPlaceholder}</option>
                    <option value="buying">{t.realEstate.interestBuying}</option>
                    <option value="selling">{t.realEstate.interestSelling}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="re-property" className="block text-sm font-semibold text-slate-300 mb-2">
                    {t.realEstate.propertyType} *
                  </label>
                  <select
                    id="re-property"
                    required
                    value={formData.property_type}
                    onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    data-testid="re-property-select"
                  >
                    <option value="">{t.realEstate.propertyPlaceholder}</option>
                    <option value="house">{t.realEstate.propertyHouse}</option>
                    <option value="apartment">{t.realEstate.propertyApartment}</option>
                    <option value="land">{t.realEstate.propertyLand}</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="re-budget" className="block text-sm font-semibold text-slate-300 mb-2">
                  {t.realEstate.budget} *
                </label>
                <select
                  id="re-budget"
                  required
                  value={formData.budget_range}
                  onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                  data-testid="re-budget-select"
                >
                  <option value="">{t.realEstate.budgetPlaceholder}</option>
                  <option value="1-2M CHF">{t.realEstate.budget1to2}</option>
                  <option value="2-3M CHF">{t.realEstate.budget2to3}</option>
                  <option value=">3M CHF">{t.realEstate.budgetOver3}</option>
                </select>
              </div>

              <div>
                <label htmlFor="re-location" className="block text-sm font-semibold text-slate-300 mb-2">
                  {t.realEstate.location}
                </label>
                <input
                  type="text"
                  id="re-location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder={t.realEstate.locationPlaceholder}
                  data-testid="re-location-input"
                />
              </div>

              <div>
                <label htmlFor="re-message" className="block text-sm font-semibold text-slate-300 mb-2">
                  {t.realEstate.message}
                </label>
                <textarea
                  id="re-message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={t.realEstate.messagePlaceholder}
                  data-testid="re-message-input"
                ></textarea>
              </div>

              <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="re-consent"
                    required
                    checked={formData.consent_given}
                    onChange={(e) => setFormData({ ...formData, consent_given: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-white/20 bg-white/10 text-[#5B7FA8] focus:ring-[#5B7FA8] accent-[#5B7FA8]"
                    data-testid="re-consent-checkbox"
                  />
                  <label htmlFor="re-consent" className="text-sm text-slate-300 leading-relaxed">
                    {t.realEstate.consent} *
                  </label>
                </div>
              </div>

              <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                <p className="text-xs text-slate-400 leading-relaxed">
                  <strong className="text-white">Datenschutz:</strong> {t.realEstate.privacyNotice}
                </p>
              </div>

              <p className="text-sm text-slate-400 italic">
                {t.realEstate.requiredFields}
              </p>

              <button
                type="submit"
                disabled={loading || !formData.consent_given}
                className="athletic-button athletic-button-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="re-submit-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    {t.realEstate.submit}...
                  </>
                ) : (
                  <>
                    <Building2 className="w-5 h-5" />
                    {t.realEstate.submit}
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Junior Trial Lesson Form Component

export default RealEstatePage;
